/**
 * Dates and times in Africa/Lusaka, formatted the PRD way: "10 Nov 2026" and
 * "10 Nov 2026, 08:00" (24-hour). Month names come from the translation files, so
 * callers pass them in; `useDateFormat` in src/i18n binds them for components.
 */

export const LUSAKA_TIME_ZONE = 'Africa/Lusaka'

export interface LusakaParts {
  year: number
  month: number // 1–12
  day: number
  hour: number // 0–23
  minute: number
}

let formatter: Intl.DateTimeFormat | null | undefined

function lusakaFormatter(): Intl.DateTimeFormat | null {
  if (formatter === undefined) {
    try {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: LUSAKA_TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        hourCycle: 'h23',
      })
    } catch {
      formatter = null // an old browser without time zone data
    }
  }
  return formatter
}

/** The calendar date and clock time in Lusaka for an instant. */
export function lusakaParts(date: Date): LusakaParts {
  const intl = lusakaFormatter()
  if (intl) {
    const parts = intl.formatToParts(date)
    const get = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((p) => p.type === type)?.value ?? NaN)
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour') % 24, minute: get('minute') }
  }
  // Zambia keeps Central Africa Time, UTC+2, all year.
  const shifted = new Date(date.getTime() + 2 * 60 * 60 * 1000)
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth() + 1,
    day: shifted.getUTCDate(),
    hour: shifted.getUTCHours(),
    minute: shifted.getUTCMinutes(),
  }
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "10 Nov 2026". `monthsShort` holds 12 names, January first. */
export function formatDate(date: Date, monthsShort: readonly string[]): string {
  const p = lusakaParts(date)
  return `${p.day} ${monthsShort[p.month - 1] ?? pad(p.month)} ${p.year}`
}

/** "08:00", 24-hour. */
export function formatTime(date: Date): string {
  const p = lusakaParts(date)
  return `${pad(p.hour)}:${pad(p.minute)}`
}

/** "10 Nov 2026, 08:00". */
export function formatDateTime(date: Date, monthsShort: readonly string[]): string {
  return `${formatDate(date, monthsShort)}, ${formatTime(date)}`
}

/** "2026-11-10", for file names. */
export function isoDate(date: Date): string {
  const p = lusakaParts(date)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/**
 * A calendar date from the API ("2026-11-10") as midday that day in Lusaka, so the
 * formatters above show the same day whatever the device's own time zone.
 */
export function parseIsoDate(text: string): Date {
  const [year = NaN, month = 1, day = 1] = text.slice(0, 10).split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day, 10, 0))
}

/** "Nov 2026" for a month ("2026-11") or any date in it. */
export function formatMonth(text: string, monthsShort: readonly string[]): string {
  const [year = '', month = '1'] = text.split('-')
  return `${monthsShort[Number(month) - 1] ?? month} ${year}`
}
